import { supabase } from "../config/Supabase";

// Client for the herb knowledge graph ("The Atlas") — the four calls in
// herb-kg/AGENT_BRIEF.md and nothing else. Traversal, ranking and the safety
// gate live in Postgres so the mobile app inherits them; this module only
// normalizes shapes, caches, and re-applies the two gates the database does
// NOT apply for us (see SAFETY GATE below).
//
// Caching follows the module-level promise cache in utils/herbLinker.js: we
// memoize the in-flight promise, not the resolved value, so N components
// mounting at once share one request. Unlike herbLinker we do NOT swallow
// errors into null — components here use the `status` state machine and need
// to distinguish "empty" from "failed" — so a rejected promise is evicted and
// rethrown, otherwise one network blip would poison the key for the session.
//
// SAFETY GATE — read before adding a call.
// "X" = Duke's "do not self-administer". 141 of 1056 herbs carry it. The four
// calls do NOT gate uniformly, measured against the loaded data:
//   kb_subgraph            gates  — p_hide_unsafe, defaults true
//   kb_formula             gates  — excludes X and non-therapeutic actions in SQL
//   kb_search              DOES NOT — 141 X herbs are searchable by name
//   kb_condition_top_herbs DOES NOT — public_score zeroes X herbs but `rank`
//     still counts them, so 698 X-rated rows sit at rank <= 20 across 398
//     conditions, some at rank 1 (erectile-dysfunction -> hedge-hyssop).
//     Filtering by rank alone would put "do not self-administer" at the top of
//     a public page. We filter on safety_score here and renumber.
// Every public caller must leave hideUnsafe on.

// ---------------------------------------------------------------- evidence

// Duke's legend, lowest to highest. Order matters: index = strength, and the
// SQL `evidence >= p_min_evidence` comparisons use this same enum ordering.
export const EVIDENCE_ORDER = [
  "folk",
  "preclinical",
  "clinical_extract",
  "clinical_plant",
];

// The brief's hardest UI rule: a claim never renders without its grade in
// words. Keep the wording here, in one place — if it lives in components it
// will drift, and "unlabeled folklore" is the one failure this product cannot
// ship. `strokeWidth` is the Atlas edge encoding (1/2/3px); folk is also 1px
// but dashed, so weakest-two stay distinguishable without relying on width.
const EVIDENCE_META = {
  folk: {
    label: "Traditional use — no science reviewed",
    short: "folk",
    strokeWidth: 1,
    dashed: true,
  },
  preclinical: {
    label: "In vitro / animal only",
    short: "preclinical",
    strokeWidth: 1,
    dashed: false,
  },
  clinical_extract: {
    label: "Human data on an extract or tea",
    short: "clinical (extract)",
    strokeWidth: 2,
    dashed: false,
  },
  clinical_plant: {
    label: "Clinical trial, whole plant",
    short: "clinical (whole plant)",
    strokeWidth: 3,
    dashed: false,
  },
};

const UNKNOWN_EVIDENCE = {
  label: "Ungraded — treat as unverified",
  short: "ungraded",
  strokeWidth: 1,
  dashed: true,
};

// Never returns undefined: a missing grade must still render *something*
// honest rather than an empty badge that reads as "no caveat".
export const describeEvidence = (grade) =>
  EVIDENCE_META[grade] || UNKNOWN_EVIDENCE;

export const isAtLeast = (grade, minimum) =>
  EVIDENCE_ORDER.indexOf(grade) >= EVIDENCE_ORDER.indexOf(minimum);

export const UNSAFE_BADGE = "X";

// --------------------------------------------------------------- the cache

// Keyed by the full argument tuple — the brief asks for (node_id, depth,
// min_evidence), but limit and hideUnsafe change the response too, so they
// are in the key as well. Bounded because the Atlas expands node by node and
// an unbounded map would grow with every click for the life of the tab.
const MAX_ENTRIES = 120;
const _cache = new Map();

const cached = (key, run) => {
  const hit = _cache.get(key);
  if (hit) {
    _cache.delete(key); // re-insert so Map order stays least-recent-first
    _cache.set(key, hit);
    return hit;
  }
  const promise = run().catch((err) => {
    _cache.delete(key); // failures must not stick, or retry is impossible
    throw err;
  });
  _cache.set(key, promise);
  if (_cache.size > MAX_ENTRIES) _cache.delete(_cache.keys().next().value);
  return promise;
};

export const clearKnowledgeGraphCache = () => _cache.clear();

const unwrap = ({ data, error }) => {
  if (error) throw error;
  return data;
};

// ------------------------------------------------------------ normalizers

const toNode = (row) => ({
  id: row.id,
  nodeType: row.node_type,
  slug: row.slug,
  label: row.label,
  sublabel: row.sublabel || null,
  badge: row.badge || null,
  isUnsafe: row.node_type === "herb" && row.badge === UNSAFE_BADGE,
});

const toEdge = (row) => {
  const refs = row.refs || [];
  return {
    src: row.src,
    dst: row.dst,
    predicate: row.predicate,
    evidence: row.evidence,
    evidenceMeta: describeEvidence(row.evidence),
    refs,
    refCount: refs.length, // the "+ source count" half of the badge rule
    weight: row.weight,
  };
};

// ------------------------------------------------------------- the 4 calls

// Search box. kb_search has no safety parameter, so the gate is applied here.
export const searchNodes = (term, options = {}) => {
  const { limit = 20, hideUnsafe = true } = options;
  const q = String(term || "").trim();
  if (q.length < 2) return Promise.resolve([]); // ILIKE '%a%' scans the world
  return cached(
    `search:${q.toLowerCase()}:${limit}:${hideUnsafe}`,
    async () => {
      // Over-fetch so the safety filter can't leave a short list: 141 of 1056
      // herbs are X-rated and they cluster in poison-adjacent queries.
      const rows =
        unwrap(
          await supabase.rpc("kb_search", {
            p_q: q,
            p_limit: hideUnsafe ? Math.min(limit * 2, 50) : limit,
          })
        ) || [];
      const nodes = rows.map(toNode);
      return (hideUnsafe ? nodes.filter((n) => !n.isUnsafe) : nodes).slice(
        0,
        limit
      );
    }
  );
};

// Click-to-expand. The whole Atlas runs on this one call; the recursive CTE
// and the safety gate are both server-side, so there is nothing to re-filter.
export const fetchSubgraph = (nodeId, options = {}) => {
  const {
    depth = 1,
    minEvidence = "preclinical",
    limitPerNode = 20,
    hideUnsafe = true,
  } = options;
  if (!nodeId) return Promise.resolve({ root: null, nodes: [], edges: [] });
  const key = `subgraph:${nodeId}:${depth}:${minEvidence}:${limitPerNode}:${hideUnsafe}`;
  return cached(key, async () => {
    const payload = unwrap(
      await supabase.rpc("kb_subgraph", {
        p_node_id: nodeId,
        p_depth: depth,
        p_min_evidence: minEvidence,
        p_limit_per_node: limitPerNode,
        p_hide_unsafe: hideUnsafe,
      })
    );
    return {
      root: payload?.root || nodeId,
      nodes: (payload?.nodes || []).map(toNode),
      edges: (payload?.edges || []).map(toEdge),
    };
  });
};

// Ranked herbs for a condition. NOT an RPC — a materialized view read, per the
// data contract. `rank` is precomputed over all herbs including X-rated ones,
// so after filtering the ranks have gaps; `displayRank` is the renumbered one
// to show, `rank` is kept for debugging against the view.
export const fetchConditionTopHerbs = (conditionId, options = {}) => {
  const { limit = 20, hideUnsafe = true } = options;
  if (!conditionId) return Promise.resolve([]);
  return cached(`topherbs:${conditionId}:${limit}:${hideUnsafe}`, async () => {
    let query = supabase
      .from("kb_condition_top_herbs")
      .select(
        "herb_id, herb_slug, primary_common_name, scientific_name, safety_score, evidence, ref_codes, n_refs, public_score, rank"
      )
      .eq("condition_id", conditionId);
    // Filter before the rank window, not after: an X herb at rank 1 would
    // otherwise consume a slot and push a usable herb off the list.
    if (hideUnsafe) query = query.neq("safety_score", UNSAFE_BADGE);
    const rows = unwrap(await query.order("rank").limit(limit)) || [];
    return rows.map((row, i) => ({
      herbId: row.herb_id,
      herbSlug: row.herb_slug,
      name: row.primary_common_name,
      scientificName: row.scientific_name,
      safetyScore: row.safety_score,
      isUnsafe: row.safety_score === UNSAFE_BADGE,
      evidence: row.evidence,
      evidenceMeta: describeEvidence(row.evidence),
      refs: row.ref_codes || [],
      refCount: row.n_refs ?? (row.ref_codes || []).length,
      rank: row.rank,
      displayRank: i + 1,
    }));
  });
};

// Formula builder. Greedy action-coverage walk; it stops early when no
// remaining herb adds a new target action, so it can return FEWER than
// p_size steps (insomnia/3 returns 2). Callers must render what they get,
// not assume `size` rows.
// The entire /conditions/:slug payload in ONE round trip.
//
// The page used to issue three sequential waves — condition by slug, then five
// parallel calls that needed its id, then monograph links that needed the herb
// ids. Waves cannot overlap, and every cross-origin call carries an apikey
// header so each is preceded by a CORS preflight: 8 queries arrived as 16
// network requests over a ~68 ms link, and preflights cache per URL so a first
// visit pays nearly all of them. Measured cost was ~1.06 s of pure round trips
// against 1–60 ms of actual server work.
//
// public.kb_condition_page does the joins in Postgres and returns one document.
// getSourceRefIndex is deliberately NOT folded in — it is the same citation
// table on every condition page and is already memoised for the session.
//
// The individual fetchers below are kept: other callers use them, and they are
// the fallback when this RPC is unavailable (a database that has not run
// migration 20260819000300 yet).
export const fetchConditionPage = (slug, options = {}) => {
  const { herbLimit = 24, actionLimit = 8, formulaSize = 3 } = options;
  if (!slug) return Promise.resolve(null);
  return cached(`condpage:${slug}:${herbLimit}`, async () => {
    const { data, error } = await supabase.rpc("kb_condition_page", {
      p_slug: slug,
      p_herb_limit: herbLimit,
      p_action_limit: actionLimit,
      p_formula_size: formulaSize,
    });
    if (error) throw error;
    if (!data) return null; // unknown or non-public slug — the RPC returns null
    // evidenceMeta is presentation, not data: the SQL returns the raw grade and
    // the wording stays in EVIDENCE_META so it cannot drift between surfaces.
    return {
      condition: data.condition,
      herbs: (data.herbs || []).map((h) => ({
        ...h,
        isUnsafe: h.safetyScore === UNSAFE_BADGE,
        evidenceMeta: describeEvidence(h.evidence),
      })),
      actions: (data.actions || []).map((a) => ({
        ...a,
        lift: Number(a.lift),
        z: Number(a.z),
      })),
      mix: data.evidenceMix || null,
      formula: (data.formula || []).map((f) => ({
        ...f,
        evidenceMeta: describeEvidence(f.evidence),
      })),
      // Same Map shape fetchMonographLinks returned, built from the joined
      // titles so the page's link logic is unchanged.
      monographs: new Map(
        (data.herbs || [])
          .filter((h) => h.monograph)
          .map((h) => [h.herbId, h.monograph])
      ),
    };
  });
};

export const fetchFormula = (conditionSlug, options = {}) => {
  const { size = 3, minEvidence = "preclinical" } = options;
  if (!conditionSlug) return Promise.resolve([]);
  return cached(`formula:${conditionSlug}:${size}:${minEvidence}`, async () => {
    const rows =
      unwrap(
        await supabase.rpc("kb_formula", {
          p_condition_slug: conditionSlug,
          p_size: size,
          p_min_evidence: minEvidence,
        })
      ) || [];
    return rows.map((row) => ({
      step: row.step,
      herbId: row.herb_id,
      herbSlug: row.herb_slug,
      name: row.herb,
      scientificName: row.scientific_name,
      safetyScore: row.safety_score,
      evidence: row.evidence,
      evidenceMeta: describeEvidence(row.evidence),
      // Therapeutic actions only — kb_formula joins polarity='therapeutic',
      // so nothing adverse can arrive here. Do not relax that server-side.
      newActions: row.new_actions || [],
      coveredRunning: row.covered_running,
      targetActions: row.target_actions,
    }));
  });
};

// ------------------------------------------------- reads for /conditions/:slug
// Beyond the brief's four calls. These are plain lookups and a CMS join, NOT
// graph traversal reassembled client-side — the traversal stays in kb_subgraph.

// Resolve a graph node from a URL slug — powers /atlas?node=insomnia&type=condition,
// the deep links the homepage Explore cards use. Slugs are NOT unique across
// node types (there is a 'poison' condition, action AND herb), so the type is
// part of the lookup rather than an afterthought.
export const fetchNodeBySlug = (slug, nodeType) => {
  if (!slug) return Promise.resolve(null);
  return cached(`node:${slug}:${nodeType || "any"}`, async () => {
    let query = supabase
      .from("kb_nodes")
      .select("id, node_type, slug, label, sublabel, badge")
      .eq("slug", slug);
    if (nodeType) query = query.eq("node_type", nodeType);
    const rows = unwrap(await query.limit(1)) || [];
    return rows.length ? toNode(rows[0]) : null;
  });
};

export const fetchConditionBySlug = (slug) => {
  if (!slug) return Promise.resolve(null);
  return cached(`condition:${slug}`, async () => {
    const { data, error } = await supabase
      .from("kb_conditions")
      .select("id, slug, label, lay_label, body_system, is_public")
      .eq("slug", slug)
      .maybeSingle();
    if (error) throw error;
    if (!data || !data.is_public) return null;
    return {
      id: data.id,
      slug: data.slug,
      // lay_label is unpopulated across all 1557 rows today, so this is `label`
      // in practice. Kept as the fallback chain so a later lay-label pass on
      // the dataset improves every page with no code change.
      label: data.lay_label || data.label,
      clinicalLabel: data.label,
      bodySystem: data.body_system || null,
    };
  });
};

// The actions that characterize a condition, by lift ("herbs used for X are
// 7.2x more likely to be sedative"). Two round trips because the lift matview
// carries action_id only and PostgREST cannot embed across a matview.
// Adverse actions cannot appear here — the matview joins polarity='therapeutic'.
export const fetchConditionActions = (conditionId, options = {}) => {
  const { limit = 8 } = options;
  if (!conditionId) return Promise.resolve([]);
  return cached(`condactions:${conditionId}:${limit}`, async () => {
    const lift =
      unwrap(
        await supabase
          .from("kb_condition_action_lift")
          .select("action_id, herbs_with_both, lift, z")
          .eq("condition_id", conditionId)
          .order("z", { ascending: false })
          .limit(limit)
      ) || [];
    if (!lift.length) return [];
    const actions =
      unwrap(
        await supabase
          .from("kb_actions")
          .select("id, slug, label, lay_label")
          .in(
            "id",
            lift.map((r) => r.action_id)
          )
      ) || [];
    const byId = new Map(actions.map((a) => [a.id, a]));
    return lift
      .map((r) => {
        const a = byId.get(r.action_id);
        if (!a) return null;
        return {
          actionId: r.action_id,
          slug: a.slug,
          label: a.lay_label || a.label,
          herbCount: r.herbs_with_both,
          lift: Number(r.lift),
          z: Number(r.z),
        };
      })
      .filter(Boolean);
  });
};

// The conditions substantive enough to have their own page. Backed by the
// kb_conditions_published view (>= 3 herbs after the X-rated exclusion) so the
// hub, the prerenderer and the sitemap can never disagree about what exists.
export const fetchPublishedConditions = () =>
  cached("published-conditions", async () => {
    const rows =
      unwrap(
        await supabase
          .from("kb_conditions_published")
          .select("id, slug, label, safe_herb_count, clinical_herb_count")
          .order("label")
      ) || [];
    return rows.map((r) => ({
      id: r.id,
      slug: r.slug,
      label: r.label,
      herbCount: r.safe_herb_count,
      clinicalCount: r.clinical_herb_count,
    }));
  });

// The evidence mix for a condition — the product thesis rendered as numbers
// ("14 of 47 herbs here rest on tradition alone"). Fetches the grade column
// only for every safe herb on the condition (max 631 rows, ~20 bytes each) and
// tallies client-side, which is one round trip instead of four count queries.
export const fetchConditionEvidenceMix = (conditionId, options = {}) => {
  const { hideUnsafe = true } = options;
  if (!conditionId) return Promise.resolve(null);
  return cached(`evmix:${conditionId}:${hideUnsafe}`, async () => {
    let query = supabase
      .from("kb_condition_top_herbs")
      .select("evidence")
      .eq("condition_id", conditionId);
    if (hideUnsafe) query = query.neq("safety_score", UNSAFE_BADGE);
    const rows = unwrap(await query) || [];
    const counts = {
      folk: 0,
      preclinical: 0,
      clinical_extract: 0,
      clinical_plant: 0,
    };
    for (const r of rows) {
      if (counts[r.evidence] !== undefined) counts[r.evidence] += 1;
    }
    const total = rows.length;
    return {
      total,
      counts,
      // "Clinical" = human data of either kind; the headline number a reader
      // actually needs in order to weigh the list.
      clinical: counts.clinical_extract + counts.clinical_plant,
      folkOnly: counts.folk,
      folkShare: total ? Math.round((counts.folk / total) * 100) : 0,
    };
  });
};

// Adverse actions for a herb — Duke's source text mixes Mutagenic and
// Teratogenic into the same list as Analgesic, and kb_actions.polarity is what
// separates them. These must be surfaced in a safety panel and must NEVER be
// rendered as a benefit. The graph edges shown elsewhere are therapeutic-only.
export const fetchHerbAdverse = (kbHerbId) => {
  if (!kbHerbId) return Promise.resolve([]);
  return cached(`adverse:${kbHerbId}`, async () => {
    const rows =
      unwrap(
        await supabase
          .from("kb_herb_adverse")
          .select("adverse_action, evidence, ref_codes")
          .eq("herb_id", kbHerbId)
      ) || [];
    return rows.map((r) => ({
      action: r.adverse_action,
      evidence: r.evidence,
      evidenceMeta: describeEvidence(r.evidence),
      refs: r.ref_codes || [],
    }));
  });
};

// Duke's citation codes ("PED", "AHP") are meaningless to a reader, so resolve
// them to their short-form citations. 121 rows, fetched once per session —
// same one-query-per-session shape as utils/herbLinker.js getHerbIndex().
let _refIndex = null;
export const getSourceRefIndex = () => {
  if (!_refIndex) {
    _refIndex = supabase
      .from("kb_source_refs")
      .select("code, citation")
      .then(({ data }) => {
        const map = new Map();
        for (const r of data || []) map.set(r.code, r.citation);
        return map;
      })
      .catch(() => new Map()); // citations degrade to raw codes, page still renders
  }
  return _refIndex;
};

// Which of these graph herbs have an editorial monograph to link to? Only 144
// of 1056 kb_herbs carry a herb_id, so most rows link nowhere — callers must
// render a plain name when the map has no entry. `herbs` has no slug column
// (see AGENT_BRIEF trap 10), so the URL is derived from the title exactly the
// way HerbPage.js does it, via the shared herbSlug helper.
export const fetchMonographLinks = (kbHerbIds) => {
  const ids = [...new Set((kbHerbIds || []).filter(Boolean))];
  if (!ids.length) return Promise.resolve(new Map());
  const key = `monolinks:${ids.slice().sort().join(",")}`;
  return cached(key, async () => {
    const rows =
      unwrap(
        await supabase
          .from("kb_herbs")
          .select("id, herb_id, herbs(id, title)")
          .in("id", ids)
          .not("herb_id", "is", null)
      ) || [];
    const map = new Map();
    for (const r of rows) {
      const title = r.herbs && r.herbs.title;
      if (title) map.set(r.id, title);
    }
    return map;
  });
};
